import { Card } from "@/components/Card";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "@/components/Select";
import { applications } from "./Applications";
import IntegrationsModal from "./NetSuite/NetsuitePreviewModal";
import useSelectedTeam from "@/hooks/useSelectedTeam";
import useApi from "@/hooks/useApi";
import {
  fetchNetSuiteConfig,
  fetchFormstackConfig,
  fetchAccelaConfig,
  fetchShopifyConfig,
  fetchHubSpotConfig,
  fetchBigCommerceConfig,
  fetchDynamicsConfig,
  fetchWooCommerceConfig,
  fetchMagentoConfig,
  fetchSalesforceConfig,
  fetchServiceNowConfig
} from "./IntegrationConfigs";
import { appCategories } from "./Categories";
import { IconProps } from "@velaro/velaro-component-lib";
import IntegrationsNewApp from "./IntegrationsNewApp";
import useSubscriptions from "@/hooks/useSubscriptions";
import useAdminFeatures from "@/hooks/useAdminFeatures";

interface NetSuiteConfig {
  id: number;
  displayName: string;
  company: string;
  teamId: number;
  createdAt: string;
}

interface FormstackConfig {
  displayName: string;
  token: string;
}

interface AccelaConfig {
  id: number;
  agencyName: string;
  clientId: string;
  clientSecret: string;
  emailAddress: string;
  accessToken: string;
  refreshToken: string;
  scope?: string;
  teamId: number;
  token?: string;
  refreshTokenExpired?: boolean;
}

interface ShopifyConfig {
  id: number;
  displayName: string;
  company: string;
  teamId: number;
  createdAt: string;
}

type App = {
  id: string;
  name: string;
  category: string;
  icon?: string | ((props: IconProps) => JSX.Element); // Accept both image paths and components
  subTitle: string;
};

interface OptionType {
  value: string | number;
  label: string;
}

const customSingleValue = ({ data }: { data: OptionType }) => (
  <div className="flex items-center gap-2">
    <img src="" alt="Selected" className="w-4 h-4" />
    {data.label}
  </div>
);

const customOption = (props: {
  data: any;
  isSelected: any;
  innerRef: any;
  innerProps: any;
}) => {
  const { data, isSelected, innerRef, innerProps } = props;
  return (
    <div
      ref={innerRef}
      {...innerProps}
      className={`px-3 py-2 cursor-pointer flex items-center gap-2 ${
        isSelected ? "bg-blue-100" : "bg-white"
      }`}
    >
      {isSelected && <img src="" alt="Selected" className="w-4 h-4" />}
      {data.label}
    </div>
  );
};
interface DetectionSuggestion {
  platform: string;
  platformLabel: string;
  integrationId: string;
  integrationName: string;
  reason: string;
  integrationUrl: string;
  dismissed: boolean;
}

export default function Landing() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [visibleCount, setVisibleCount] = useState(12);
  const [selectedApp, setSelectedApp] = useState<App | null>(null);
  const [showIntegrationsModal, setShowIntegrationsModal] = useState(false);
  const [suggestions, setSuggestions] = useState<DetectionSuggestion[]>([]);
  const [suggestionsDismissed, setSuggestionsDismissed] = useState(false);
  const [netSuiteConfigs, setNetSuiteConfigs] = useState<NetSuiteConfig[]>([]);
  const [formstackConfig, setFormstackConfig] =
    useState<FormstackConfig | null>(null);
  const [accelaConfigs, setAccelaConfigs] = useState<AccelaConfig | null>(null);
  const [shopifyConfigs, setShopifyConfigs] = useState<ShopifyConfig | null>(null);
  const [hubSpotConfigs, setHubSpotConfigs] = useState<any[] | null>(null);
  const [bigCommerceConfigs, setBigCommerceConfigs] = useState<any[] | null>(null);
  const [dynamicsConfigs, setDynamicsConfigs] = useState<any[] | null>(null);
  const [wooCommerceConfigs, setWooCommerceConfigs] = useState<any[] | null>(null);
  const [magentoConfigs, setMagentoConfigs] = useState<any[] | null>(null);
  const [salesforceConfigs, setSalesforceConfigs] = useState<any[] | null>(null);
  const [serviceNowConfigs, setServiceNowConfigs] = useState<any[] | null>(null);
  const [uplistingConfigs, setUplistingConfigs] = useState<any[] | null>(null);
  const [guestyConfigs, setGuestyConfigs] = useState<any[] | null>(null);
  const [cloudbedsConfigs, setCloudbedsConfigs] = useState<any[] | null>(null);
  const { subscription } = useSubscriptions();
  const { enableMcp, enableHubSpot, enableBigCommerce, enableDynamics, enableTeamsNotifications, enableSlackNotifications, enableMagento } = useAdminFeatures();
  const api = useApi();
  const { teamId } = useSelectedTeam();
  const [appStatus, setAppStatuses] = useState<{ [key: string]: string }>({});
  const [showNewAppPanel, setShowNewAppPanel] = useState(false);

  useEffect(() => {
    const fetchStatuses = async () => {
      const newStatuses: { [key: string]: string } = {};

      if (netSuiteConfigs.length > 0) {
        newStatuses["netsuite"] = await checkIfMappingExists("netsuite");
      }

      if (formstackConfig != null) {
        newStatuses["formstack"] = "Connected";
      }

      if (accelaConfigs != null) {
        newStatuses["accela-civic"] = "Connected";
      }

      if (shopifyConfigs != null) {
        newStatuses["shopify"] = "Connected";
      }

      if (hubSpotConfigs != null && hubSpotConfigs.length > 0) {
        newStatuses["hubspot"] = "Connected";
      }

      if (bigCommerceConfigs != null && bigCommerceConfigs.length > 0) {
        newStatuses["bigcommerce"] = "Connected";
      }

      if (magentoConfigs != null && magentoConfigs.length > 0) {
        newStatuses["magento"] = "Connected";
      }

      if (salesforceConfigs != null && salesforceConfigs.length > 0) {
        newStatuses["salesforce"] = "Connected";
      }

      if (serviceNowConfigs != null && serviceNowConfigs.length > 0) {
        newStatuses["servicenow"] = "Connected";
      }

      if (dynamicsConfigs != null && dynamicsConfigs.length > 0) {
        newStatuses["dynamics"] = "Connected";
      }

      if (uplistingConfigs != null && uplistingConfigs.length > 0) {
        newStatuses["uplisting"] = "Connected";
      }

      if (guestyConfigs != null && guestyConfigs.length > 0) {
        newStatuses["guesty"] = "Connected";
      }

      if (cloudbedsConfigs != null && cloudbedsConfigs.length > 0) {
        newStatuses["cloudbeds"] = "Connected";
      }

      try {
        const mcpResp = await api.admin.get("McpServers");
        if (mcpResp.ok) {
          const mcpData = await mcpResp.json();
          if (Array.isArray(mcpData) && mcpData.some((s: { enabled: boolean }) => s.enabled)) {
            newStatuses["mcp"] = "Connected";
          }
        }
      } catch {
        // non-critical — MCP badge just won't show
      }

      try {
        const teamsRes = await api.admin.get("TeamsNotification");
        const teamsData = await teamsRes.json();
        if (Array.isArray(teamsData) && teamsData.some((c: { isEnabled: boolean }) => c.isEnabled)) {
          newStatuses["teams-notifications"] = "Connected";
        }
      } catch { /* non-critical */ }

      try {
        const slackRes = await api.admin.get("SlackNotification");
        const slackData = await slackRes.json();
        if (Array.isArray(slackData) && slackData.some((c: { isEnabled: boolean }) => c.isEnabled)) {
          newStatuses["slack-notifications"] = "Connected";
        }
      } catch { /* non-critical */ }

      try {
        const calRes  = await api.admin.get("OutlookCalendar/Status");
        const calData = await calRes.json();
        if (calData?.configured) newStatuses["outlook-calendar"] = "Connected";
      } catch { /* non-critical */ }

      try {
        const clyRes  = await api.admin.get("Calendly/Status");
        const clyData = await clyRes.json();
        if (clyData?.configured) newStatuses["calendly"] = "Connected";
      } catch { /* non-critical */ }

      try {
        const sqRes  = await api.admin.get("Square/Status");
        const sqData = await sqRes.json();
        if (sqData?.configured) newStatuses["square-appointments"] = "Connected";
      } catch { /* non-critical */ }

      try {
        const klavRes  = await api.admin.get("Klaviyo/Status");
        const klavData = await klavRes.json();
        if (klavData?.connected) newStatuses["klaviyo"] = "Connected";
      } catch { /* non-critical */ }

      try {
        const stripeRes  = await api.admin.get("Stripe/Config");
        const stripeData = await stripeRes.json();
        if (stripeData?.configured) newStatuses["stripe-payments"] = "Connected";
      } catch { /* non-critical */ }

      setAppStatuses(newStatuses);
    };

    fetchStatuses();
  }, [netSuiteConfigs, formstackConfig, accelaConfigs, shopifyConfigs, hubSpotConfigs, bigCommerceConfigs, magentoConfigs, dynamicsConfigs, salesforceConfigs, serviceNowConfigs, uplistingConfigs, guestyConfigs, cloudbedsConfigs, api.admin]);

  const [configId, setConfigId] = useState<string | "">("");
  const checkIfMappingExists = async (appId: string): Promise<string> => {
    if (appId === "netsuite" && netSuiteConfigs.length > 0) {
      const latestConfigId =
        [...netSuiteConfigs].sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )[0]?.id || "";

      setConfigId(latestConfigId ? String(latestConfigId) : "");

      try {
        const resp = await api.messaging.get(
          `NetSuite/Integrations/Mapping/${latestConfigId}`
        );
        const count = await resp.json();

        return count > 0 ? "Connected" : "Pending";
      } catch (error) {
        console.error("Error fetching NetSuite config:", error);
        return "Error";
      }
    }

    return "";
  };
  const filteredApps =
    selectedCategory === "all"
      ? applications
      : selectedCategory === "my-apps"
      ? applications.filter(
          (app) => app.id !== "new" && !!appStatus[app.id]
        )
      : applications.filter((app) => app.category === selectedCategory);

  const showApps = filteredApps.sort((a, b) => {
    // "Request New App" always first
    if (a.id === "new") return -1;
    if (b.id === "new") return 1;
    // Connected integrations come first
    const aConnected = !!appStatus[a.id];
    const bConnected = !!appStatus[b.id];
    if (aConnected && !bConnected) return -1;
    if (!aConnected && bConnected) return 1;
    return 0;
  });

  React.useEffect(() => {
    setVisibleCount(12);
  }, [selectedCategory]);

  const handleLoadMore = () => {
    setVisibleCount((prevCount) => prevCount + 6);
  };

  const handleCardClick = async (app: App) => {
    if (app.id === "new") {
      setShowNewAppPanel(true);
      return;
    }

    const hasConfig = checkIfConfigExists(app);
    if (hasConfig) {
      navigate(`/integrations/${app.id}`);
    } else {
      if (app.id === "formstack") {
        navigate(`/integrations/${app.id}/${app.id}create`);
      } else if (app.id === "accela-civic") {
        navigate(`/integrations/${app.id}/accelacreate`);
      } else if (app.id === "shopify") {
        navigate(`/integrations/${app.id}/shopifyoverview`);
      } else if (app.id === "hubspot") {
        navigate(`/integrations/hubspot/overview`);
      } else if (app.id === "bigcommerce") {
        navigate(`/integrations/bigcommerce/overview`);
      } else if (app.id === "magento") {
        navigate(`/integrations/magento/overview`);
      } else if (app.id === "dynamics") {
        navigate(`/integrations/dynamics/overview`);
      } else if (app.id === "mcp") {
        navigate(`/integrations/mcp`);
      } else if (app.id === "teams-notifications") {
        navigate(`/integrations/teams-notifications`);
      } else if (app.id === "slack-notifications") {
        navigate(`/integrations/slack-notifications`);
      } else if (app.id === "outlook-calendar") {
        navigate(`/integrations/outlook-calendar`);
      } else if (app.id === "calendly") {
        navigate(`/integrations/calendly`);
      } else if (app.id === "square-appointments") {
        navigate(`/integrations/square-appointments`);
      } else if (app.id === "power-automate") {
        navigate(`/integrations/power-automate`);
      } else if (app.id === "make") {
        navigate(`/integrations/make`);
      } else if (app.id === "zapier") {
        navigate(`/integrations/zapier`);
      } else if (app.id === "woocommerce") {
        navigate(`/integrations/woocommerce`);
      } else if (app.id === "stripe-payments") {
        navigate(`/integrations/payments/stripe`);
      } else if (app.id === "klaviyo") {
        navigate(`/integrations/klaviyo`);
      } else if (app.id === "velaro-crm") {
        navigate(`/integrations/velaro-crm`);
      } else if (app.id === "salesforce") {
        navigate(`/integrations/salesforce`);
      } else if (app.id === "gorgias") {
        navigate(`/integrations/gorgias`);
      } else if (app.id === "google-tag-manager") {
        navigate(`/integrations/google-tag-manager`);
      } else if (app.id === "servicenow") {
        navigate(`/integrations/servicenow`);
      } else if (app.id === "uplisting") {
        navigate(`/integrations/uplisting`);
      } else if (app.id === "guesty") {
        navigate(`/integrations/guesty`);
      } else if (app.id === "cloudbeds") {
        navigate(`/integrations/cloudbeds`);
      } else if (app.id === "microsoft-365") {
        navigate(`/integrations/microsoft-365`);
      } else if (app.id === "transcript-storage") {
        navigate(`/integrations/transcript-storage`);
      } else {
        setSelectedApp(app);
        setShowIntegrationsModal(true);
      }
    }
  };

  const checkIfConfigExists = (app: App): boolean => {
    if (app.id === "netsuite") {
      return netSuiteConfigs.length > 0;
    } else if (app.id === "formstack") {
      return formstackConfig !== null;
    } else if (app.id === "accela-civic") {
      return accelaConfigs !== null;
    } else if (app.id === "shopify") {
      return shopifyConfigs !== null;
    } else if (app.id === "hubspot") {
      return hubSpotConfigs !== null && hubSpotConfigs.length > 0;
    } else if (app.id === "bigcommerce") {
      return bigCommerceConfigs !== null && bigCommerceConfigs.length > 0;
    } else if (app.id === "magento") {
      return magentoConfigs !== null && magentoConfigs.length > 0;
    } else if (app.id === "dynamics") {
      return dynamicsConfigs !== null && dynamicsConfigs.length > 0;
    } else if (app.id === "mcp") {
      // Always navigate directly — MCP has its own management page
      return true;
    } else if (app.id === "teams-notifications" || app.id === "slack-notifications") {
      return true;
    } else if (app.id === "woocommerce") {
      // Always navigate directly — WooCommerce has its own management page
      return true;
    } else if (app.id === "stripe-payments") {
      return true;
    } else if (app.id === "klaviyo") {
      // Always navigate directly — Klaviyo has its own management page
      return true;
    } else if (app.id === "gorgias") {
      // Always navigate directly — Gorgias wizard has its own page
      return true;
    } else if (app.id === "salesforce") {
      // Always navigate directly — Salesforce has its own management page
      return true;
    } else if (app.id === "google-tag-manager") {
      // Always navigate directly — GTM has its own page
      return true;
    } else if (app.id === "servicenow") {
      return true;
    } else if (app.id === "uplisting") {
      return uplistingConfigs != null && uplistingConfigs.length > 0;
    } else if (app.id === "guesty") {
      return guestyConfigs != null && guestyConfigs.length > 0;
    } else if (app.id === "cloudbeds") {
      return cloudbedsConfigs != null && cloudbedsConfigs.length > 0;
    } else if (app.id === "microsoft-365") {
      return true;
    } else if (app.id === "transcript-storage") {
      return true;
    } else if (app.id === "square-appointments") {
      return true;
    } else if (app.id === "outlook-calendar" || app.id === "calendly" || app.id === "power-automate" || app.id === "make" || app.id === "zapier") {
      // These always navigate to their own page regardless of config state
      return true;
    }
    return false;
  };

  React.useEffect(() => {
    async function loadConfigs() {
      if (teamId) {
        const netSuiteData = await fetchNetSuiteConfig(api, teamId);
        if (netSuiteData) setNetSuiteConfigs(netSuiteData);
      }
      const formstackData = await fetchFormstackConfig(api);
      if (formstackData) setFormstackConfig(formstackData);

      const accelaData = await fetchAccelaConfig(
        api,
        subscription?.enableAccela
      );
      if (accelaData) setAccelaConfigs(accelaData);

      const shopifyData = await fetchShopifyConfig(
        api,
        subscription?.enableShopify
      );
      if (shopifyData) setShopifyConfigs(shopifyData);

      const hubSpotData = await fetchHubSpotConfig(api, enableHubSpot);
      if (hubSpotData) setHubSpotConfigs(hubSpotData);

      const bigCommerceData = await fetchBigCommerceConfig(api, enableBigCommerce);
      if (bigCommerceData) setBigCommerceConfigs(bigCommerceData);

      const dynamicsData = await fetchDynamicsConfig(api, enableDynamics);
      if (dynamicsData) setDynamicsConfigs(dynamicsData);

      const magentoData = await fetchMagentoConfig(api, enableMagento);
      if (magentoData) setMagentoConfigs(magentoData);

      const salesforceData = await fetchSalesforceConfig(api);
      if (salesforceData) setSalesforceConfigs(salesforceData);

      const serviceNowData = await fetchServiceNowConfig(api);
      if (serviceNowData) setServiceNowConfigs(serviceNowData);

      try {
        const uplistingResp = await api.admin.get("Uplisting/Configs");
        if (uplistingResp.ok) {
          const uplistingData = await uplistingResp.json();
          if (Array.isArray(uplistingData)) setUplistingConfigs(uplistingData);
        }
      } catch { /* non-critical */ }

      try {
        const guestyResp = await api.admin.get("Guesty/Configs");
        if (guestyResp.ok) {
          const guestyData = await guestyResp.json();
          if (Array.isArray(guestyData)) setGuestyConfigs(guestyData);
        }
      } catch { /* non-critical */ }

      try {
        const cloudbedsResp = await api.admin.get("Cloudbeds/Configs");
        if (cloudbedsResp.ok) {
          const cloudbedsData = await cloudbedsResp.json();
          if (Array.isArray(cloudbedsData)) setCloudbedsConfigs(cloudbedsData);
        }
      } catch { /* non-critical */ }
    }

    loadConfigs();

    // Load platform detection suggestions (non-blocking)
    api.admin.get("SiteDetection/Suggestions").then(async (res) => {
      if (res.ok) {
        const data = await res.json();
        setSuggestions(Array.isArray(data.suggestions) ? data.suggestions : []);
        setSuggestionsDismissed(!!data.dismissed);
      }
    }).catch(() => {/* non-critical */});
  }, [api, teamId, enableHubSpot, enableBigCommerce, enableDynamics, enableMagento]);

  const handleModalClose = () => {
    setShowIntegrationsModal(false);
  };

  const handleDismissSuggestions = async () => {
    setSuggestionsDismissed(true);
    try { await api.admin.post("SiteDetection/Suggestions/Dismiss", {}); } catch { /* non-critical */ }
  };

  const modifiedCategories = appCategories.map((cat) => ({
    ...cat,
    label: cat.value === selectedCategory ? ` ${cat.label}` : cat.label
  }));

  const activeSuggestions = suggestions.filter(s => !s.dismissed && !suggestionsDismissed);

  return (
    <div className="panel bg-white dark:bg-slate-800 rounded-2xl shadow-md mb-4 p-4 xl:max-w-[1350px] xl:justify-center xl:mx-auto">

      {/* ── Detection Suggestions Banner ─────────────────────────────────── */}
      {activeSuggestions.length > 0 && (
        <div className="mb-5 border border-blue-200 bg-blue-50 dark:bg-blue-950 dark:border-blue-800 rounded-xl p-4">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div>
              <p className="text-sm font-semibold text-blue-800 dark:text-blue-200">
                💡 We detected tools on your site — want to connect them?
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                Based on your widget activity. Connect these integrations to unlock AI-powered features.
              </p>
            </div>
            <button
              onClick={handleDismissSuggestions}
              className="text-blue-400 hover:text-blue-600 text-lg leading-none flex-shrink-0"
              aria-label="Dismiss suggestions"
            >×</button>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {activeSuggestions.map((s) => (
              <button
                key={s.integrationId + s.platform}
                onClick={() => navigate(s.integrationUrl)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded-lg text-sm text-blue-800 dark:text-blue-200 hover:bg-blue-50 dark:hover:bg-blue-800 transition-colors"
                title={s.reason}
              >
                <span className="font-medium">{s.integrationName}</span>
                <span className="text-blue-400 dark:text-blue-500">→</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-between items-center mb-4">
        <div className="text-center text-slate-800 text-lg font-medium font-['Rubik'] leading-7">
          All Apps
          <span className="text-center text-slate-500 text-lg font-medium font-['Rubik'] leading-7 ml-2">
            {showApps.filter((app) => app.id !== "new").length} apps
          </span>
        </div>
        <style>{`select option[value=""] { display: none; }`}</style>
        <Select
          className="w-48 h-12 text-sm px-3 py-2 border rounded-md !w-48"
          value={selectedCategory}
          options={modifiedCategories}
          onChange={(value) => setSelectedCategory(value)}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 netsuite-card">
        {showApps.slice(0, visibleCount).map((app) => (
          <Card
            key={app.id}
            to=""
            title={app.name}
            subtitle={app.subTitle}
            icon={app.icon}
            variant="integrations"
            isFirstCard={app.id === "new"}
            onClick={() => {
              handleCardClick(app);
            }}
            appStatus={String(appStatus[app.id] || "")}
          />
        ))}
      </div>

      {visibleCount < showApps.length && (
        <div className="flex justify-center mt-4">
          <button
            onClick={handleLoadMore}
            className="text-blue-600 font-medium focus:outline-none"
          >
            Load More ({showApps.length - visibleCount} remaining)
          </button>
        </div>
      )}

      {showIntegrationsModal && selectedApp && (
        <IntegrationsModal
          show={showIntegrationsModal}
          onClose={handleModalClose}
          appId={selectedApp.id}
          appName={selectedApp.name}
          icon={selectedApp.icon || ""}
          teamId={teamId}
          appStatus={appStatus[selectedApp.id] || ""}
          configId={configId}
        />
      )}

      <div
        className={`fixed top-0 right-0 h-full w-1/4 bg-white shadow-lg transition-transform duration-300 ${
          showNewAppPanel ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <IntegrationsNewApp onClose={() => setShowNewAppPanel(false)} />
      </div>
    </div>
  );
}
